import { SubscriptionModel } from '@mm/state/model/SubscriptionModel';
import {
  mapPlanAllotmentOptions,
  PlanInfo,
} from '@mm/state/helpers/billing-info-helpers';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { SubscriptionResponse } from '@mm/state/interfaces/subscription';
import {
  enhancedBillingPagesSelector,
  selectAnnualPlansAllotments,
  selectPlansAllotments,
} from '@mm/state/selectors/facebookPageSelector';
import {
  enhancedPlansSelector,
  getCurrentSubscription,
  selectAccountDetails,
} from '@mm/state/selectors/subscriptionSelectors';
import { abbreviateNumber, centsToDollars } from '@mm/utils';
import { capitalize, isEmpty, isNumber } from 'lodash';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useFreeTrialUpgradeModal } from '../../../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';

import { useMemo } from 'react';
import { getUsedAndTotal, planCanManageOverages } from '../helpers';

// tslint:disable-next-line:cyclomatic-complexity
export const useBillingDetails = (
  page: EnhancedBillingPage,
  planInfo?: Partial<PlanInfo>,
  teamSubscription?: SubscriptionModel,
) => {
  const currentSubscription =
    useSelector(getCurrentSubscription(page?.id)) || teamSubscription;
  const enhancedPlans = useSelector(enhancedPlansSelector);
  const monthlyPlansAllotments = useSelector(selectPlansAllotments);
  const annualPlansAllotments = useSelector(selectAnnualPlansAllotments);
  const accountDetails = useSelector(selectAccountDetails);
  const connectedPages = useSelector(enhancedBillingPagesSelector);

  const plan = enhancedPlans.filter(
    p => p.planCode === currentSubscription?.planCode,
  );

  const plansAllotments =
    currentSubscription?.billingPeriodLength === 'annually'
      ? annualPlansAllotments
      : monthlyPlansAllotments;

  const plansAllotmentsOptions = mapPlanAllotmentOptions({
    plansAllotments,
    getExtraAllotment: false,
    page,
    plan,
    useFiltered: false,
  });
  const extraPlansAllotmentsOptions = mapPlanAllotmentOptions({
    plansAllotments,
    getExtraAllotment: true,
    page,
    plan,
    useFiltered: false,
  });

  const {
    isFreeTrialPromotionWithFutureSubscription,
    isFreeTrialPromotionActive,
    isFreeTrialPromotionWithoutSubscription,
  } = useFreeTrialUpgradeModal();

  const billingPeriod =
    currentSubscription?.billingPeriodLength === 'annually'
      ? 'year'
      : (currentSubscription?.planIntervalLength || 0) > 1
      ? 'months'
      : 'month';

  const subscriptionTerm =
    (currentSubscription?.planIntervalLength || 0) > 1
      ? `${currentSubscription?.planIntervalLength} ${capitalize(
          billingPeriod,
        )}`
      : currentSubscription?.billingPeriodLength === 'annually'
      ? 'Annual'
      : 'Monthly';

  const subscriptionStatus = isFreeTrialPromotionWithFutureSubscription
    ? 'Future'
    : capitalize(
        teamSubscription ? teamSubscription.state : currentSubscription?.state,
      ) || '';

  const nextBillPlanName = enhancedPlans.find(
    p => p.planCode === currentSubscription?.planCode,
  )?.shortName;

  const nextBillingAmount = currentSubscription?.expiresAt
    ? 'n/a'
    : `$${centsToDollars(currentSubscription?.nextInvoiceAmount?.sum || 0)} `;

  const subscriptionPrice = currentSubscription?.currentInvoiceAmount?.subsPrice
    ? `$${centsToDollars(currentSubscription?.currentInvoiceAmount?.subsPrice)}`
    : currentSubscription?.canceledAt
    ? 'n/a'
    : isNumber(currentSubscription?.basePrice)
    ? `$${centsToDollars(currentSubscription?.basePrice || 0)}`
    : 'Free';

  const nextBillSubscription = nextBillPlanName
    ? `${nextBillPlanName} subscription (${subscriptionPrice}) `
    : '';

  const nextBillSeatsPrice =
    currentSubscription?.nextInvoiceAmount?.allAddOns?.userSeats?.priceTotal ||
    currentSubscription?.nextInvoiceAmount?.fixedAddOns?.userSeats?.priceTotal;

  const seatsPrice = nextBillSeatsPrice
    ? `$${centsToDollars(nextBillSeatsPrice)}`
    : currentSubscription
    ? '-'
    : '';

  const thisPeriodSeats =
    currentSubscription?.currentInvoiceAmount?.allAddOns?.userSeats?.quantity ||
    currentSubscription?.currentInvoiceAmount?.fixedAddOns?.userSeats?.quantity;

  const nextPeriodSeats =
    currentSubscription?.nextInvoiceAmount?.allAddOns?.userSeats?.quantity ||
    currentSubscription?.nextInvoiceAmount?.fixedAddOns?.userSeats?.quantity;

  const nextBillExtraSeats =
    (seatsPrice.match(/\d+/) || 0) > 0
      ? ` + ${nextPeriodSeats} extra user seats (${seatsPrice})`
      : '';

  const sendsPrice = currentSubscription?.nextInvoiceAmount?.allAddOns
    ?.sendAllotment?.priceTotal
    ? `$${centsToDollars(
        currentSubscription?.nextInvoiceAmount?.allAddOns.sendAllotment
          .priceTotal,
      )}`
    : currentSubscription?.active
    ? '-'
    : '';

  const sendCreditsInfo =
    page?.messagesStatistic &&
    getUsedAndTotal(page.messagesStatistic, currentSubscription);

  const thisPeriodCredits = abbreviateNumber(
    plansAllotmentsOptions.find(
      o =>
        o.level ===
        currentSubscription?.currentInvoiceAmount?.allAddOns?.sendAllotment
          ?.quantity,
    )?.name ||
      sendCreditsInfo?.[1] ||
      plan?.[0]?.outgoingMessagesLimit ||
      '',
  );

  const nextPeriodExtraCredits = abbreviateNumber(
    extraPlansAllotmentsOptions.find(
      o =>
        o.level ===
        currentSubscription?.nextInvoiceAmount?.allAddOns?.sendAllotment
          ?.quantity,
    )?.name || '',
  );

  const nextBillExtraSends =
    (sendsPrice.match(/\d+/) || 0) > 0
      ? ` + ${nextPeriodExtraCredits} extra sends (${sendsPrice})`
      : '';

  const nextPeriodTotalCredits = abbreviateNumber(
    ['canceled', 'expired'].find(s => s === currentSubscription?.state)
      ? 1000
      : plansAllotmentsOptions.find(
          o =>
            o.level ===
            currentSubscription?.nextInvoiceAmount?.allAddOns?.sendAllotment
              ?.quantity,
        )?.name ||
          plan?.[0]?.outgoingMessagesLimit ||
          '',
  );

  const nextBillAdditionalPagesPrice =
    currentSubscription?.nextInvoiceAmount?.allAddOns?.additionalPages
      ?.priceTotal ||
    currentSubscription?.nextInvoiceAmount?.fixedAddOns?.additionalPages
      ?.priceTotal;

  const additionalPagesPrice = nextBillAdditionalPagesPrice
    ? `$${centsToDollars(nextBillAdditionalPagesPrice)}`
    : '';

  const thisPeriodAdditionalPages =
    currentSubscription?.currentInvoiceAmount?.allAddOns?.additionalPages
      ?.quantity ||
    currentSubscription?.currentInvoiceAmount?.fixedAddOns?.additionalPages
      ?.quantity;

  const nextPeriodAdditionalPages =
    currentSubscription?.nextInvoiceAmount?.allAddOns?.additionalPages
      ?.quantity ||
    currentSubscription?.nextInvoiceAmount?.fixedAddOns?.additionalPages
      ?.quantity;

  const nextBillAdditionalPages = additionalPagesPrice
    ? ` + ${nextPeriodAdditionalPages} Additional pages (${additionalPagesPrice})`
    : '';

  const nextBillSMSNumberPrice =
    currentSubscription?.nextInvoiceAmount?.allAddOns?.smsNumber?.priceTotal ||
    currentSubscription?.nextInvoiceAmount?.fixedAddOns?.smsNumber?.priceTotal;

  const smsNumberPrice = nextBillSMSNumberPrice
    ? ` $${centsToDollars(nextBillSMSNumberPrice)}`
    : '';

  const thisPeriodSmsNumberQuantity =
    currentSubscription?.currentInvoiceAmount?.allAddOns?.smsNumber?.quantity ||
    currentSubscription?.currentInvoiceAmount?.fixedAddOns?.smsNumber?.quantity;

  const nextPeriodSmsNumberQuantity =
    currentSubscription?.nextInvoiceAmount?.allAddOns?.smsNumber?.quantity ||
    currentSubscription?.nextInvoiceAmount?.fixedAddOns?.smsNumber?.quantity;

  const nextBillSmsNumber = smsNumberPrice
    ? ` + ${nextPeriodSmsNumberQuantity} SMS numbers${smsNumberPrice}`
    : '';

  const thisPeriodBillCombinedInfo = `$${centsToDollars(
    currentSubscription?.currentInvoiceAmount?.sum || 0,
  )?.toLocaleString()}`;

  const nextBillCombinedInfo =
    nextBillingAmount +
    nextBillSubscription +
    nextBillExtraSeats +
    nextBillAdditionalPages +
    nextBillSmsNumber +
    nextBillExtraSends;

  const getLastCardNumbers = (accountDetails?: SubscriptionResponse) =>
    accountDetails && accountDetails.account
      ? accountDetails.account.lastCardNumbers
      : 'XXXX';

  const lastCardNumbers = getLastCardNumbers(accountDetails);
  const billedWith = `XXXX-XXXX-XXXX-${lastCardNumbers}`;

  const getSubscriptionIsCanceled = (subscription?: SubscriptionModel) => {
    if (subscription) {
      return subscription.state === 'canceled' || subscription.downgradePending;
    }
  };

  const subscriptionIsCanceled = getSubscriptionIsCanceled(
    page?.subscription || teamSubscription,
  );

  const billingPeriodEndsAt = moment(
    teamSubscription ? teamSubscription.endsAt : page?.subscription?.endsAt,
  ).format('MM/DD/YYYY');

  const subscriptionEndsAt = subscriptionIsCanceled
    ? billingPeriodEndsAt
    : 'n/a';

  const nextBillingDate =
    !subscriptionIsCanceled && subscriptionEndsAt === 'n/a'
      ? billingPeriodEndsAt
      : 'n/a';

  const isFreeTrialWithHardLimitReach =
    isFreeTrialPromotionActive && page?.hardLimitReached;

  const anyPageHasSmsActivated = useMemo(
    () =>
      connectedPages.filter(p => !isEmpty(p.subscription?.smsNumbers)).length >
      0,
    [connectedPages],
  );

  const isAllowedByLockStrategy =
    page?.lockStrategy === 'fb_page_level' ||
    (page?.lockStrategy === 'twilio_level' && anyPageHasSmsActivated);

  const canManageExtraSends =
    !isFreeTrialPromotionActive ||
    (teamSubscription && isAllowedByLockStrategy);

  const hasSubscriptions = page?.subscriptions?.length;

  const canManageOverages =
    (hasSubscriptions &&
      planInfo &&
      planCanManageOverages(page?.outgoingMessagesLimit, planInfo) &&
      !subscriptionIsCanceled) ||
    (teamSubscription && isAllowedByLockStrategy) ||
    (isFreeTrialWithHardLimitReach && !isFreeTrialPromotionWithoutSubscription);

  const allotmentLevelDowngrade =
    currentSubscription?.addOnDowngrades?.allotmentLevelDowngrade;

  const isPageWithOutgoingMessagesLimits = !!(
    hasSubscriptions && (page?.outgoingMessagesLimit || 0) > 0
  );

  const shouldShowCredits =
    (!hasSubscriptions && !page?.redemption) ||
    !!page?.redemption?.freeTrial ||
    (planInfo?.isAnyTeamPlan &&
      isAllowedByLockStrategy &&
      isPageWithOutgoingMessagesLimits) ||
    isPageWithOutgoingMessagesLimits;

  return {
    currentSubscription,
    subscriptionTerm,
    subscriptionStatus,
    subscriptionEndsAt,
    subscriptionIsCanceled,
    nextBillingAmount,
    nextBillPlanName,
    smsNumberPrice,
    sendsPrice,
    subscriptionPrice,
    seatsPrice,
    additionalPagesPrice,
    nextBillAdditionalPages,
    nextBillSmsNumber,
    thisPeriodBillCombinedInfo,
    nextBillCombinedInfo,
    nextBillSubscription,
    nextBillingDate,
    nextBillExtraSends,
    nextBillExtraSeats,
    thisPeriodCredits,
    nextPeriodTotalCredits,
    thisPeriodSeats,
    nextPeriodSeats,
    billingPeriod,
    billedWith,
    thisPeriodSmsNumberQuantity,
    thisPeriodAdditionalPages,
    plansAllotmentsOptions,
    shouldShowCredits,
    canManageOverages,
    canManageExtraSends,
    isAllowedByLockStrategy,
    allotmentLevelDowngrade,
  };
};
